<template>
  <div>
    <div class="sidebar-top d-print-none">
      <h3 class="mb-5">{{ $t('feedback.title') }}</h3>
      <p>{{ $t('feedback.hint') }}</p>
      <p>{{ $t('form.allFieldsRequired') }}</p>

      <ValidationGroup alwaysSubmit @submit="send" ref="form">
        <template v-slot:default="{ touched, submitted, valid }">
          <ValidationField class="form-group" :required="!isLoggedIn" :value="details.name" highlight>
            <template v-slot:default>
              <label for="fname">{{ $t('feedback.labels.name') }}</label>
              <input
                v-if="!isLoggedIn"
                id="fname"
                class="form-control"
                v-model="details.name"
              >
              <span v-else class="filled-form">{{ details.name }}</span>
            </template>
            <template v-slot:error>
              <span class="invalid-feedback">{{ $t('feedback.validation.name') }}</span>
            </template>
          </ValidationField>
          <ValidationField class="form-group" email required :value="details.email" highlight>
            <template v-slot:default>
              <label for="fmail">{{ $t('feedback.labels.email') }}</label>
              <input type="email" class="form-control" id="fmail" v-model="details.email">
            </template>
            <template v-slot:error>
              <span class="invalid-feedback">{{ $t('feedback.validation.email') }}</span>
            </template>
          </ValidationField>
          <ValidationField class="form-group" required v-if="isLoggedIn" :value="reason" highlight>
            <template v-slot:default>
              <label for="reasonId">{{ $t('feedback.labels.reason') }}</label>
              <select class="custom-select form-control" id="reasonId" v-model="reason">
                <option value="" />
                <option v-for="reason in $tm('feedback.reasons')" :value="reason" :key="reason.title">
                  {{ reason.title }}
                </option>
              </select>
              <span class="caption gray-600" v-if="reason && reason.hint" v-html="reason.hint" />
            </template>
            <template v-slot:error>
              <span class="invalid-feedback">{{ $t('feedback.validation.reason') }}</span>
            </template>
          </ValidationField>
          <ValidationField class="form-group" required :minlength="minlength" :value="details.text" highlight>
            <template v-slot:default>
              <label for="ftext">{{ $t('feedback.labels.question') }}</label>
              <textarea class="form-control" id="ftext" v-model="details.text" />
            </template>
            <template v-slot:error>
              <span class="invalid-feedback">{{ $t('feedback.validation.question') }}</span>
            </template>
          </ValidationField>
          <ValidationField
            v-if="!isLoggedIn"
            class="form-group"
            type="checkbox"
            required
            :value="isRecaptchaVerified"
            highlight
          >
            <ReCaptcha @change="isRecaptchaVerified = $event" ref="recaptcha" />
          </ValidationField>
          <Messages id="feedback" v-if="!touched || submitted && !valid" />
          <button class="btn btn-lg btn-success btn-block mt-4" type="submit">
            {{ $t('feedback.labels.submit') }}
          </button>
        </template>
      </ValidationGroup>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ReCaptcha from '../components/ReCaptcha';
import ValidationGroup from '../components/ValidationGroup';
import ValidationField from '../components/ValidationField';
import Messages from '../components/Messages';

export default {
  components: {
    ReCaptcha,
    ValidationField,
    ValidationGroup,
    Messages,
  },
  data: () => ({
    minlength: import.meta.env.VITE_FEEDBACK_TEXT_MINLENGTH,
    isRecaptchaVerified: false,
    reason: null,
    details: {
      name: '',
      email: '',
      text: '',
    },
  }),
  watch: {
    profile: {
      immediate: true,
      handler: 'syncWithProfile',
    },
    details: {
      deep: true,
      handler() {
        this.clearMessages('feedback');
      },
    },
    reason() {
      this.clearMessages('feedback');
    },
  },
  computed: {
    ...mapState('session', ['profile']),
    ...mapGetters('session', ['isLoggedIn']),
  },
  methods: {
    ...mapActions('app', ['sendQuestion']),
    ...mapActions('alerts', ['showMessage', 'clearMessages']),

    syncWithProfile() {
      if (this.profile) {
        this.details.name = this.profile.name;
        this.details.email = this.profile.email;
      }
    },

    async reset() {
      Object.assign(this.$data, this.$options.data.call(this));
      this.syncWithProfile();
      await this.clearMessages('feedback');

      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset();
      }

      this.$refs.form.reset();
    },

    showValidationError() {
      return this.showMessage({
        target: 'feedback',
        type: 'danger',
        text: this.$t('feedback.validation.error'),
      });
    },

    send({ valid }) {
      if (!valid) {
        return this.showValidationError();
      }

      const params = { ...this.details };

      if (this.isLoggedIn) {
        params.reason = this.reason.title;
        params.reasonCode = this.reason.code;
      }

      return this.sendQuestion(params)
        .then(async () => {
          await this.reset();
          this.showMessage({ target: 'feedback', type: 'success', text: this.$t('feedback.success') });
        })
        .catch(() => {
          this.showMessage({ target: 'feedback', type: 'danger', text: this.$t('feedback.error') });
        });
    },
  },
};
</script>

<style scoped>
  textarea.form-control {
    height: 10rem;
  }
</style>
